(function (root) {
  'use strict';

  var doc = root.document;
  var allowedRates = [0.75, 1, 1.25, 1.5, 2];
  var states = typeof WeakMap === 'function' ? new WeakMap() : null;

  function finiteNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function formatTime(seconds) {
    var safeSeconds = Math.max(0, Math.floor(finiteNumber(seconds, 0)));
    var hours = Math.floor(safeSeconds / 3600);
    var minutes = Math.floor((safeSeconds % 3600) / 60);
    var remainingSeconds = safeSeconds % 60;
    var paddedSeconds = String(remainingSeconds).padStart(2, '0');
    if (hours > 0) return hours + ':' + String(minutes).padStart(2, '0') + ':' + paddedSeconds;
    return minutes + ':' + paddedSeconds;
  }

  function isEligible(video) {
    if (!video || video.nodeName === 'AUDIO') return false;
    if (video.dataset && video.dataset.anfVideoEnhanced === 'true') return false;
    if (typeof video.matches === 'function' && video.matches('[data-anf-no-controls]')) return false;
    if (video.autoplay && video.muted) return false;

    var hasNativeControls = Boolean(video.controls);
    if (!hasNativeControls && typeof video.hasAttribute === 'function') hasNativeControls = video.hasAttribute('controls');
    var interactiveContainer = null;
    if (typeof video.closest === 'function') {
      interactiveContainer = video.closest('internal-video[data-autoplay="false"], .vid-fr, .anf-sleep-vid-fr, .sp-vid-frame');
    }
    return hasNativeControls || Boolean(interactiveContainer);
  }

  function seekTo(video, seconds) {
    if (!video) return 0;
    var duration = finiteNumber(video.duration, 0);
    var nextTime = clamp(finiteNumber(seconds, 0), 0, Math.max(0, duration));
    video.currentTime = nextTime;
    return nextTime;
  }

  function seekBy(video, delta) {
    return seekTo(video, finiteNumber(video && video.currentTime, 0) + finiteNumber(delta, 0));
  }

  function setPlaybackRate(video, rate) {
    var nextRate = finiteNumber(rate, 1);
    if (!video || allowedRates.indexOf(nextRate) === -1) return false;
    video.playbackRate = nextRate;
    return true;
  }

  function toggleMuted(video) {
    if (!video) return false;
    video.muted = !video.muted;
    return video.muted;
  }

  function releaseBlockingOverlay(video) {
    if (!video || typeof video.closest !== 'function') return;
    var internalVideo = video.closest('internal-video');
    if (!internalVideo) return;
    if (internalVideo.classList) internalVideo.classList.add('anf-video-overlay-released');
    var overlay = typeof internalVideo.querySelector === 'function' ? internalVideo.querySelector('.internal-video__play') : null;
    if (!overlay) return;
    if (overlay.style) overlay.style.pointerEvents = 'none';
    if (overlay.style) overlay.style.display = 'none';
    if (typeof overlay.setAttribute === 'function') overlay.setAttribute('aria-hidden', 'true');
    overlay.tabIndex = -1;
  }

  function icon(name) {
    var icons = {
      play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"></path></svg>',
      pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zm6 0h4v14h-4z"></path></svg>',
      volume: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4zm11.5 3a4.5 4.5 0 0 0-2-3.74v7.48A4.5 4.5 0 0 0 15.5 12zm0-8.3v2.08A7 7 0 0 1 19 12a7 7 0 0 1-3.5 6.22v2.08A9 9 0 0 0 21 12a9 9 0 0 0-5.5-8.3z"></path></svg>',
      muted: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4zm11.3 1.3-1.4 1.4 2.1 2.1-2.1 2.1 1.4 1.4 2.1-2.1 2.1 2.1 1.4-1.4-2.1-2.1 2.1-2.1-1.4-1.4-2.1 2.1z"></path></svg>',
      fullscreen: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5h6v2H7v4H5V5zm8 0h6v6h-2V7h-4V5zM5 13h2v4h4v2H5v-6zm12 0h2v6h-6v-2h4v-4z"></path></svg>'
    };
    return icons[name] || '';
  }

  function controlsMarkup() {
    return '' +
      '<div class="anf-video-controls" data-anf-controls>' +
        '<label class="anf-video-seek-label"><span class="visually-hidden">Video position</span><input class="anf-video-seek" data-anf-control="seek" type="range" min="0" max="0" step="0.1" value="0" aria-label="Video position"></label>' +
        '<div class="anf-video-actions">' +
          '<button type="button" class="anf-video-button anf-video-toggle" data-anf-action="toggle" aria-label="Play video">' + icon('play') + '</button>' +
          '<button type="button" class="anf-video-button anf-video-jump" data-anf-action="back" aria-label="Go back 10 seconds"><span aria-hidden="true">−10</span></button>' +
          '<button type="button" class="anf-video-button anf-video-jump" data-anf-action="forward" aria-label="Go forward 10 seconds"><span aria-hidden="true">+10</span></button>' +
          '<output class="anf-video-time" data-anf-control="time" aria-live="off">0:00 / 0:00</output>' +
          '<span class="anf-video-spacer" aria-hidden="true"></span>' +
          '<label class="anf-video-rate-label"><span>Speed</span><select class="anf-video-rate" data-anf-control="rate" aria-label="Playback speed"><option value="0.75">0.75x</option><option value="1" selected>1x</option><option value="1.25">1.25x</option><option value="1.5">1.5x</option><option value="2">2x</option></select></label>' +
          '<button type="button" class="anf-video-button anf-video-mute" data-anf-action="mute" aria-label="Mute video">' + icon('volume') + '</button>' +
          '<label class="anf-video-volume-label"><span class="visually-hidden">Volume</span><input class="anf-video-volume" data-anf-control="volume" type="range" min="0" max="1" step="0.05" value="1" aria-label="Volume"></label>' +
          '<button type="button" class="anf-video-button anf-video-fullscreen" data-anf-action="fullscreen" aria-label="Enter fullscreen">' + icon('fullscreen') + '</button>' +
        '</div>' +
      '</div>';
  }

  function playVideo(video) {
    var result = video.play();
    if (result && typeof result.catch === 'function') result.catch(function () {});
  }

  function togglePlayback(video) {
    if (video.paused || video.ended) playVideo(video);
    else video.pause();
  }

  function enterFullscreen(player, video) {
    if (player.requestFullscreen) return player.requestFullscreen();
    if (player.webkitRequestFullscreen) return player.webkitRequestFullscreen();
    if (video.webkitEnterFullscreen) return video.webkitEnterFullscreen();
    return null;
  }

  function enhance(video) {
    if (!doc || !isEligible(video)) return null;
    if (states && states.has(video)) return states.get(video).player;

    var originalParent = video.parentNode;
    if (!originalParent) return null;
    var hadNativeControls = video.hasAttribute('controls') || Boolean(video.controls);
    var player = doc.createElement('div');
    player.className = 'anf-video-player';
    player.dataset.anfVideoPlayer = 'true';
    originalParent.insertBefore(player, video);
    player.appendChild(video);

    var controls = doc.createElement('div');
    controls.innerHTML = controlsMarkup();
    controls = controls.firstElementChild;
    player.appendChild(controls);
    releaseBlockingOverlay(video);

    var toggle = controls.querySelector('[data-anf-action="toggle"]');
    var back = controls.querySelector('[data-anf-action="back"]');
    var forward = controls.querySelector('[data-anf-action="forward"]');
    var mute = controls.querySelector('[data-anf-action="mute"]');
    var fullscreen = controls.querySelector('[data-anf-action="fullscreen"]');
    var seek = controls.querySelector('[data-anf-control="seek"]');
    var time = controls.querySelector('[data-anf-control="time"]');
    var rate = controls.querySelector('[data-anf-control="rate"]');
    var volume = controls.querySelector('[data-anf-control="volume"]');
    var listeners = [];

    function listen(target, eventName, handler, options) {
      target.addEventListener(eventName, handler, options);
      listeners.push([target, eventName, handler, options]);
    }

    function updateDuration() {
      var duration = Math.max(0, finiteNumber(video.duration, 0));
      seek.max = String(duration);
      time.textContent = formatTime(video.currentTime) + ' / ' + formatTime(duration);
    }

    function updateTime() {
      var duration = Math.max(0, finiteNumber(video.duration, 0));
      var current = clamp(finiteNumber(video.currentTime, 0), 0, duration || 0);
      seek.value = String(current);
      var progress = duration > 0 ? (current / duration) * 100 : 0;
      seek.style.setProperty('--anf-video-progress', progress + '%');
      time.textContent = formatTime(current) + ' / ' + formatTime(duration);
    }

    function updatePlayback() {
      var isPlaying = !video.paused && !video.ended;
      player.classList.toggle('is-playing', isPlaying);
      toggle.innerHTML = icon(isPlaying ? 'pause' : 'play');
      toggle.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
      if (isPlaying) releaseBlockingOverlay(video);
    }

    function updateVolume() {
      volume.value = String(clamp(finiteNumber(video.volume, 1), 0, 1));
      var isMuted = Boolean(video.muted) || Number(volume.value) === 0;
      mute.innerHTML = icon(isMuted ? 'muted' : 'volume');
      mute.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
    }

    function updateRate() {
      var currentRate = finiteNumber(video.playbackRate, 1);
      if (allowedRates.indexOf(currentRate) !== -1) rate.value = String(currentRate);
    }

    listen(toggle, 'click', function () { togglePlayback(video); });
    listen(back, 'click', function () { seekBy(video, -10); updateTime(); });
    listen(forward, 'click', function () { seekBy(video, 10); updateTime(); });
    listen(seek, 'input', function () { seekTo(video, seek.value); updateTime(); });
    listen(rate, 'change', function () { setPlaybackRate(video, rate.value); updateRate(); });
    listen(mute, 'click', function () { toggleMuted(video); updateVolume(); });
    listen(volume, 'input', function () {
      video.volume = clamp(finiteNumber(volume.value, 1), 0, 1);
      video.muted = video.volume === 0;
      updateVolume();
    });
    listen(fullscreen, 'click', function () {
      var fullscreenResult = enterFullscreen(player, video);
      if (fullscreenResult && typeof fullscreenResult.catch === 'function') fullscreenResult.catch(function () {});
    });
    listen(video, 'click', function (event) { if (event.target === video) togglePlayback(video); });
    listen(video, 'loadedmetadata', updateDuration);
    listen(video, 'durationchange', updateDuration);
    listen(video, 'timeupdate', updateTime);
    listen(video, 'play', function () {
      if (doc && typeof doc.querySelectorAll === 'function') {
        doc.querySelectorAll('.anf-video-player video').forEach(function (otherVideo) {
          if (otherVideo !== video && !otherVideo.paused) otherVideo.pause();
        });
      }
      updatePlayback();
    });
    listen(video, 'pause', updatePlayback);
    listen(video, 'ended', updatePlayback);
    listen(video, 'ratechange', updateRate);
    listen(video, 'volumechange', updateVolume);
    listen(player, 'keydown', function (event) {
      if (event.target !== player && event.target !== video) return;
      if (event.key === ' ' || event.key.toLowerCase() === 'k') {
        event.preventDefault();
        togglePlayback(video);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        seekBy(video, -10);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        seekBy(video, 10);
      }
    });

    video.dataset.anfVideoEnhanced = 'true';
    video.classList.add('anf-video-element');
    video.tabIndex = 0;
    video.removeAttribute('controls');
    video.controls = false;
    updateDuration();
    updateTime();
    updatePlayback();
    updateVolume();
    updateRate();

    if (states) states.set(video, { player: player, originalParent: originalParent, hadNativeControls: hadNativeControls, listeners: listeners });
    return player;
  }

  function destroy(video) {
    if (!states || !states.has(video)) return false;
    var state = states.get(video);
    state.listeners.forEach(function (entry) { entry[0].removeEventListener(entry[1], entry[2], entry[3]); });
    state.originalParent.insertBefore(video, state.player);
    state.player.remove();
    delete video.dataset.anfVideoEnhanced;
    video.classList.remove('anf-video-element');
    if (state.hadNativeControls) {
      video.setAttribute('controls', '');
      video.controls = true;
    }
    states.delete(video);
    return true;
  }

  function enhanceAll(container) {
    if (!doc) return 0;
    var scope = container || doc;
    var candidates = [];
    if (scope.nodeName === 'VIDEO') candidates.push(scope);
    if (typeof scope.querySelectorAll === 'function') scope.querySelectorAll('video').forEach(function (video) { candidates.push(video); });
    var enhancedCount = 0;
    candidates.forEach(function (video) { if (enhance(video)) enhancedCount += 1; });
    return enhancedCount;
  }

  var api = {
    allowedRates: allowedRates.slice(),
    clamp: clamp,
    destroy: destroy,
    enhance: enhance,
    enhanceAll: enhanceAll,
    formatTime: formatTime,
    isEligible: isEligible,
    releaseBlockingOverlay: releaseBlockingOverlay,
    seekBy: seekBy,
    seekTo: seekTo,
    setPlaybackRate: setPlaybackRate,
    toggleMuted: toggleMuted
  };
  root.ANFVideoControls = api;

  if (!doc) return;

  function start() {
    enhanceAll(doc);
    if ('MutationObserver' in root) {
      var observer = new root.MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (node && (node.nodeType === 1 || node.nodeName === 'VIDEO')) enhanceAll(node);
          });
        });
      });
      observer.observe(doc.documentElement, { childList: true, subtree: true });
    }
    doc.addEventListener('shopify:section:load', function (event) { enhanceAll(event.target); });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})(typeof window !== 'undefined' ? window : globalThis);
